/**
 * useFinanceSettlement hooks — Issue #370.
 *
 * 通过 @jinzhuan/api-client 调用付款申请 / 借支 / 报销接口。
 * 禁止前端伪状态。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type PaymentApplicationSummaryDto =
  ApiComponents['schemas']['PaymentApplicationSummaryDto'];
export type PaymentApplicationDetailDto =
  ApiComponents['schemas']['PaymentApplicationDetailDto'];
export type PaymentApplicationListResponseDto =
  ApiComponents['schemas']['PaymentApplicationListResponseDto'];
export type CreatePaymentApplicationDto =
  ApiComponents['schemas']['CreatePaymentApplicationDto'];
export type EmployeeAdvanceSummaryDto =
  ApiComponents['schemas']['EmployeeAdvanceSummaryDto'];
export type EmployeeAdvanceDetailDto =
  ApiComponents['schemas']['EmployeeAdvanceDetailDto'];
export type EmployeeAdvanceListResponseDto =
  ApiComponents['schemas']['EmployeeAdvanceListResponseDto'];
export type CreateEmployeeAdvanceDto =
  ApiComponents['schemas']['CreateEmployeeAdvanceDto'];
export type ReimbursementSummaryDto =
  ApiComponents['schemas']['ReimbursementSummaryDto'];
export type ReimbursementDetailDto =
  ApiComponents['schemas']['ReimbursementDetailDto'];
export type ReimbursementListResponseDto =
  ApiComponents['schemas']['ReimbursementListResponseDto'];
export type CreateReimbursementDto =
  ApiComponents['schemas']['CreateReimbursementDto'];
export type ReimbursementPrintPreviewDto =
  ApiComponents['schemas']['ReimbursementPrintPreviewDto'];

export type PaymentMethod = NonNullable<
  CreatePaymentApplicationDto['paymentMethod']
>;

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401)
    return {
      message: '会话已失效，请重新登录后再试',
      code: 'UNAUTHENTICATED',
    };
  if (effective === 403)
    return { message: '没有权限执行该操作', code: 'FORBIDDEN' };
  if (effective === 404)
    return { message: '记录不存在或已被删除', code: 'NOT_FOUND' };
  if (effective === 409)
    return {
      message: '数据已被其他用户修改，请刷新后重试',
      code: 'CONFLICT',
    };
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

// ─── Payment Applications ────────────────────────────────────────────────────

export interface ListPaymentApplicationsFilter {
  status?: string;
  counterpartyId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function usePaymentApplications() {
  const [data, setData] = useState<PaymentApplicationListResponseDto | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: ListPaymentApplicationsFilter) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number> = {};
        if (filter?.status) query.status = filter.status;
        if (filter?.counterpartyId)
          query.counterpartyId = filter.counterpartyId;
        if (filter?.search) query.search = filter.search;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/finance/payment-applications',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function usePaymentApplicationActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreatePaymentApplicationDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payment-applications',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = useCallback(async (id: string, expectedRecordVersion: number) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/payment-applications/{id}/submit',
        { params: { path: { id } }, body: { expectedRecordVersion } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const departmentReview = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reviewerName?: string; reason?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/department-review',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const financeReview = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reviewerName?: string; reason?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/finance-review',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const extraApprove = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reviewerName?: string; reason?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/extra-approve',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cashierExecute = useCallback(
    async (
      id: string,
      params: {
        expectedRecordVersion: number;
        paymentMethod: PaymentMethod;
        executedAt: string;
        executedByName?: string;
        referenceNumber?: string;
        remarks?: string;
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/cashier-execute',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const departmentReject = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/department-reject',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const financeReject = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/finance-reject',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/cancel',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const returnToDraft = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/payment-applications/{id}/return-to-draft',
          { params: { path: { id } }, body: { expectedRecordVersion } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    create,
    submit,
    departmentReview,
    financeReview,
    extraApprove,
    cashierExecute,
    departmentReject,
    financeReject,
    cancel,
    returnToDraft,
  };
}

// ─── Employee Advances ───────────────────────────────────────────────────────

export interface ListAdvancesFilter {
  status?: string;
  employeeUserId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useEmployeeAdvances() {
  const [data, setData] = useState<EmployeeAdvanceListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter?: ListAdvancesFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.employeeUserId) query.employeeUserId = filter.employeeUserId;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/employee-advances',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useEmployeeAdvanceActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreateEmployeeAdvanceDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/employee-advances',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = useCallback(async (id: string, expectedRecordVersion: number) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/employee-advances/{id}/submit',
        { params: { path: { id } }, body: { expectedRecordVersion } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const departmentReview = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reviewerName?: string; reason?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/department-review',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const financeReview = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reviewerName?: string; reason?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/finance-review',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const departmentReject = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/department-reject',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const financeReject = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/finance-reject',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cashierExecute = useCallback(
    async (
      id: string,
      params: {
        expectedRecordVersion: number;
        paymentMethod: PaymentMethod;
        executedAt: string;
        executedByName?: string;
        referenceNumber?: string;
        remarks?: string;
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/cashier-execute',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/cancel',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const returnToDraft = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/employee-advances/{id}/return-to-draft',
          { params: { path: { id } }, body: { expectedRecordVersion } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    create,
    submit,
    departmentReview,
    financeReview,
    departmentReject,
    financeReject,
    cashierExecute,
    cancel,
    returnToDraft,
  };
}

// ─── Reimbursements ──────────────────────────────────────────────────────────

export interface ListReimbursementsFilter {
  status?: string;
  applicantUserId?: string;
  originalAdvanceId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useReimbursements() {
  const [data, setData] = useState<ReimbursementListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter?: ListReimbursementsFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.applicantUserId) query.applicantUserId = filter.applicantUserId;
      if (filter?.originalAdvanceId)
        query.originalAdvanceId = filter.originalAdvanceId;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data: ok, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/expense-reimbursements',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setData(ok ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useReimbursementActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreateReimbursementDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/expense-reimbursements',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const printPreview = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/finance/expense-reimbursements/{id}/print-preview',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const submit = useCallback(async (id: string, expectedRecordVersion: number) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/finance/expense-reimbursements/{id}/submit',
        { params: { path: { id } }, body: { expectedRecordVersion } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const departmentReview = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reviewerName?: string; reason?: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/department-review',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const departmentReject = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/department-reject',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const financeReview = useCallback(
    async (
      id: string,
      params: {
        expectedRecordVersion: number;
        reviewerName?: string;
        reason?: string;
        accountantName?: string;
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/finance-review',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const financeReject = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/finance-reject',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cashierExecute = useCallback(
    async (
      id: string,
      params: {
        expectedRecordVersion: number;
        paymentMethod: PaymentMethod;
        executedAt: string;
        executedByName?: string;
        referenceNumber?: string;
        remarks?: string;
      },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/cashier-execute',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      params: { expectedRecordVersion: number; reason: string },
    ) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/cancel',
          { params: { path: { id } }, body: params },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const returnToDraft = useCallback(
    async (id: string, expectedRecordVersion: number) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/finance/expense-reimbursements/{id}/return-to-draft',
          { params: { path: { id } }, body: { expectedRecordVersion } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    error,
    create,
    printPreview,
    submit,
    departmentReview,
    departmentReject,
    financeReview,
    financeReject,
    cashierExecute,
    cancel,
    returnToDraft,
  };
}
